/* ============================================================================
   CRUX — WebGL hero  ·  assets/crux-webgl-hero.js
   Silk/ink displacement shader over the hero's OWN slide-1 image (the one the
   operator set in the theme editor). Ported from the CRUX OS mockup shader
   (vet deliverable 2026-07-02), hardened for production:

     • Progressive enhancement ONLY — the <img> stays underneath; any failure
       (no WebGL, blocked texture, context loss) is a visual no-op.
     • Loads AFTER the page 'load' event + idle, so it can never compete with
       LCP. The canvas fades in over the already-painted image.
     • DPR capped (1.75 desktop / 1.5 small screens); ~6KB, no libraries.
     • Pauses off-screen (IntersectionObserver) and on hidden tabs.
     • Honors prefers-reduced-motion + Save-Data → never boots.
     • Gated by the "webgl_hero_enabled" theme setting (script only loads then).
   ============================================================================ */
(function () {
  'use strict';

  try {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  } catch (e) {}
  try {
    var conn = navigator.connection;
    if (conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || ''))) return;
  } catch (e) {}

  var VS = 'attribute vec2 p;void main(){gl_Position=vec4(p,0.,1.);}';
  var FS =
    'precision highp float;uniform vec2 R;uniform float T;uniform vec2 M;uniform sampler2D img;uniform vec2 IR;' +
    'float h(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}' +
    'float n(vec2 p){vec2 i=floor(p),f=fract(p);f=f*f*(3.-2.*f);' +
    'return mix(mix(h(i),h(i+vec2(1,0)),f.x),mix(h(i+vec2(0,1)),h(i+vec2(1,1)),f.x),f.y);}' +
    'float fbm(vec2 p){float v=0.,a=.5;for(int i=0;i<5;i++){v+=a*n(p);p*=2.03;a*=.5;}return v;}' +
    'void main(){' +
    'vec2 uv=gl_FragCoord.xy/R;' +
    'float ra=R.x/R.y, ia=IR.x/IR.y; vec2 tuv=uv;' +
    'if(ra>ia){tuv.y=(uv.y-.5)*(ia/ra)+.5;} else {tuv.x=(uv.x-.5)*(ra/ia)+.5;}' +
    'float t=T*.12;' +
    'vec2 q=vec2(fbm(uv*3.+t),fbm(uv*3.-t*.7));' +
    'float mm=smoothstep(.45,.0,distance(uv,M));' +
    'vec2 disp=(q-.5)*.03+(uv-M)*mm*.025;' +
    'vec3 c=texture2D(img,tuv+disp).rgb;' +
    'float ink=fbm(uv*2.2-t*.4);' +
    'c=mix(c,c*vec3(.97,.955,.93),.35);' +
    'c*=.86+.22*ink;' +
    'c+=(h(gl_FragCoord.xy+T)-.5)*.045;' +
    'c*=1.-.22*pow(distance(uv,vec2(.5,.45)),1.7);' +
    'gl_FragColor=vec4(c,1.);}';

  function findHeroImage() {
    // First IMAGE slide of the hero (video slides keep their video — the
    // shader needs a still texture). The canvas lives INSIDE the slide, so
    // the slideshow carries it in and out naturally. Prefer the image variant
    // that is actually painted at this viewport (desktop vs mobile art).
    var slides = document.querySelectorAll('.hero__media-slide');
    for (var s = 0; s < slides.length; s++) {
      var slide = slides[s];
      if (slide.querySelector('video')) continue;
      var imgs = slide.querySelectorAll('img.hero__image');
      if (!imgs.length) continue;
      for (var i = 0; i < imgs.length; i++) {
        var st = getComputedStyle(imgs[i]);
        if (st.display !== 'none') return { slide: slide, img: imgs[i] };
      }
    }
    return null;
  }

  function boot() {
    var found = findHeroImage();
    if (!found) return;
    var slide = found.slide, img = found.img;

    function start() {
      var src = img.currentSrc || img.src;
      if (!src) return;

      var cv = document.createElement('canvas');
      cv.className = 'cx-glhero';
      cv.setAttribute('aria-hidden', 'true');
      var gl;
      try { gl = cv.getContext('webgl', { antialias: false, alpha: false, powerPreference: 'low-power' }); }
      catch (e) { gl = null; }
      if (!gl) return;

      function kill() {
        try { running = false; if (cv.parentNode) cv.parentNode.removeChild(cv); } catch (e) {}
      }

      function sh(type, srcCode) {
        var o = gl.createShader(type);
        gl.shaderSource(o, srcCode); gl.compileShader(o);
        if (!gl.getShaderParameter(o, gl.COMPILE_STATUS)) return null;
        return o;
      }
      var v = sh(gl.VERTEX_SHADER, VS), f = sh(gl.FRAGMENT_SHADER, FS);
      if (!v || !f) return;
      var pr = gl.createProgram();
      gl.attachShader(pr, v); gl.attachShader(pr, f); gl.linkProgram(pr);
      if (!gl.getProgramParameter(pr, gl.LINK_STATUS)) return;
      gl.useProgram(pr);
      gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
      var loc = gl.getAttribLocation(pr, 'p');
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
      var uR = gl.getUniformLocation(pr, 'R'), uT = gl.getUniformLocation(pr, 'T'),
          uM = gl.getUniformLocation(pr, 'M'), uIR = gl.getUniformLocation(pr, 'IR');

      var tex = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, 1, 1, 0, gl.RGB, gl.UNSIGNED_BYTE, new Uint8Array([17, 17, 17]));
      var iw = 1, ih = 1, texReady = false;

      var tint = new Image();
      tint.crossOrigin = 'anonymous';
      tint.onload = function () {
        try {
          iw = tint.width; ih = tint.height;
          gl.bindTexture(gl.TEXTURE_2D, tex);
          gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
          gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, tint);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
          texReady = true;
          cv.classList.add('is-live');
        } catch (e) { kill(); }
      };
      tint.onerror = kill;
      tint.src = src;

      slide.appendChild(cv);
      cv.addEventListener('webglcontextlost', function (e) { e.preventDefault(); kill(); });

      var mx = 0.5, my = 0.45;
      var fine = false;
      try { fine = window.matchMedia('(pointer: fine)').matches; } catch (e) {}
      if (fine) {
        window.addEventListener('pointermove', function (e) {
          mx = e.clientX / window.innerWidth;
          my = 1 - e.clientY / window.innerHeight;
        }, { passive: true });
      }

      function resize() {
        var small = window.innerWidth < 750;
        var d = Math.min(window.devicePixelRatio || 1, small ? 1.5 : 1.75);
        var w = cv.clientWidth || slide.clientWidth, h = cv.clientHeight || slide.clientHeight;
        if (!w || !h) return;
        cv.width = Math.round(w * d); cv.height = Math.round(h * d);
        gl.viewport(0, 0, cv.width, cv.height);
      }
      resize();
      window.addEventListener('resize', resize, { passive: true });

      var running = true, visible = true, t0 = performance.now();
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (es) {
          es.forEach(function (en) { visible = en.isIntersecting; });
        }, { threshold: 0 }).observe(slide);
      }
      document.addEventListener('visibilitychange', function () {
        if (!document.hidden && running) requestAnimationFrame(frame);
      });

      function frame() {
        if (!running) return;
        if (document.hidden || !visible || !texReady) {
          setTimeout(function () { requestAnimationFrame(frame); }, 250);
          return;
        }
        gl.uniform2f(uR, cv.width, cv.height);
        gl.uniform1f(uT, (performance.now() - t0) / 1000);
        gl.uniform2f(uM, mx, my);
        gl.uniform2f(uIR, iw, ih);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        requestAnimationFrame(frame);
      }
      requestAnimationFrame(frame);
    }

    // never compete with LCP: wait for the hero image + window load + idle
    function whenIdle(fn) {
      if ('requestIdleCallback' in window) requestIdleCallback(fn, { timeout: 2500 });
      else setTimeout(fn, 900);
    }
    function afterLoad(fn) {
      if (document.readyState === 'complete') whenIdle(fn);
      else window.addEventListener('load', function () { whenIdle(fn); }, { once: true });
    }
    if (img.complete && img.naturalWidth) afterLoad(start);
    else img.addEventListener('load', function () { afterLoad(start); }, { once: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
